import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, UserIcon, LogOutIcon, SettingsIcon, ChevronDownIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, CheckCheckIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { loadAvatarUrl, loadNotifPrefs } from '../../pages/SettingsPage';
import { AppNotification } from '../../types';
interface HeaderProps {
  title: string;
  subtitle?: string;
  onNavigate?: (page: string) => void;
}
const notifTypeConfig: Record<AppNotification['type'], {
  icon: React.ReactNode;
  color: string;
  bg: string;
}> = {
  visita_completada: {
    icon: <MapPinIcon className="w-4 h-4" data-id="element-53" />,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  },
  nuevo_cobro: {
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-54" />,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  },
  nueva_venta: {
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-55" />,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  },
  reporte_diario: {
    icon: <FileTextIcon className="w-4 h-4" data-id="element-56" />,
    color: 'text-violet-600',
    bg: 'bg-violet-100'
  },
  alerta_equipo: {
    icon: <UsersIcon className="w-4 h-4" data-id="element-57" />,
    color: 'text-amber-600',
    bg: 'bg-amber-100'
  },
  recordatorio_visita: {
    icon: <CalendarIcon className="w-4 h-4" data-id="element-58" />,
    color: 'text-rose-600',
    bg: 'bg-rose-100'
  }
};
const prefToType: Record<string, AppNotification['type']> = {
  visitasCompletadas: 'visita_completada',
  nuevosCobros: 'nuevo_cobro',
  nuevasVentas: 'nueva_venta',
  reportesDiarios: 'reporte_diario',
  alertasEquipo: 'alerta_equipo',
  recordatoriosVisita: 'recordatorio_visita'
};
function timeAgo(date: Date): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}
export function Header({
  title,
  subtitle,
  onNavigate
}: HeaderProps) {
  const {
    user,
    logout,
    notifications,
    markNotificationsRead
  } = useAuth();
  const {
    newOrderAlert,
    clearNewOrderAlert,
    orders
  } = useApp();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [prefs, setPrefs] = useState(loadNotifPrefs);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const pendingOrdersCount = orders.filter(o => o.estado === 'pendiente').length;
  useEffect(() => {
    if (newOrderAlert) {
      const timer = setTimeout(() => clearNewOrderAlert(), 8000);
      return () => clearTimeout(timer);
    }
  }, [newOrderAlert, clearNewOrderAlert]);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const activeTypes = new Set(Object.entries(prefs).filter(([, enabled]) => enabled).map(([key]) => prefToType[key]).filter(Boolean));
  const visibleNotifs = notifications.filter(n => activeTypes.has(n.type));
  const unreadCount = visibleNotifs.filter(n => !n.read).length;
  const handleOpenNotif = () => {
    setNotifOpen(!notifOpen);
    setDropdownOpen(false);
  };
  const handleCloseNotif = () => {
    setNotifOpen(false);
    markNotificationsRead();
  };
  const handleMarkAllRead = () => {
    markNotificationsRead();
  };
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleAvatarUpdate = () => setAvatarUrl(loadAvatarUrl(user?.id));
    const handleNotifsUpdate = () => setPrefs(loadNotifPrefs());
    window.addEventListener('avatar-updated', handleAvatarUpdate);
    window.addEventListener('notifs-updated', handleNotifsUpdate);
    window.addEventListener('notifications-updated', () => {});
    return () => {
      window.removeEventListener('avatar-updated', handleAvatarUpdate);
      window.removeEventListener('notifs-updated', handleNotifsUpdate);
    };
  }, []);
  useEffect(() => {
    if (user) {
      setAvatarUrl(loadAvatarUrl(user.id));
    } else {
      setAvatarUrl('');
    }
  }, [user?.id]);
  return <header className="bg-white border-b border-slate-200 relative shadow-[0_1px_2px_rgba(0,0,0,0.02)] z-20" data-id="element-59">
      <div className="px-4 lg:px-8 py-3 lg:py-5" data-id="element-60">
        <div className="flex items-center justify-between" data-id="element-61">
          {/* Left side: Logo + title */}
          <div className="flex items-center gap-3" data-id="element-62">
            <div className="w-8 h-8 lg:w-9 lg:h-9 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100 flex-shrink-0 overflow-hidden p-1" data-id="element-63">
              <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-64" />
            </div>
            {/* Desktop: full company name + page title */}
            <div className="hidden lg:block" data-id="element-65">
              <h1 className="text-lg font-bold text-slate-900 font-heading tracking-tight leading-tight" data-id="element-66">
                Miladys Rodríguez & Asoc.
              </h1>
              <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5 mt-0.5" data-id="element-67">
                <span className="w-1 h-1 rounded-full bg-[#2952C4]" data-id="element-68" />
                {title}
                {subtitle && <span className="text-slate-300 mx-0.5" data-id="element-69">—</span>}
                {subtitle && <span className="text-slate-400" data-id="element-70">{subtitle}</span>}
              </p>
            </div>
            {/* Mobile: just page title */}
            <div className="lg:hidden" data-id="element-71">
              <h1 className="text-sm font-bold text-slate-900 font-heading tracking-tight leading-tight" data-id="element-72">
                {title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:gap-3" data-id="element-73">
            {/* New order alert banner */}
            {newOrderAlert && <div className="flex items-center gap-1 bg-amber-100 border border-amber-300 rounded-xl animate-pulse shadow-sm" data-id="element-74">
                <button onClick={() => onNavigate?.('pedidos')} className="flex items-center gap-2 px-2 lg:px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-200 rounded-l-xl transition-colors" data-id="element-75">
                  <ShoppingCartIcon className="w-4 h-4" data-id="element-76" />
                  <span className="hidden sm:inline" data-id="element-77">¡Nuevo Pedido!</span>
                  <span className="sm:hidden" data-id="element-78">Pedido</span>
                </button>
                <button onClick={e => {
              e.stopPropagation();
              clearNewOrderAlert();
            }} className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-200 rounded-r-xl transition-colors" title="Cerrar alerta" data-id="element-79">
                  <XIcon className="w-3.5 h-3.5" data-id="element-80" />
                </button>
              </div>}

            {/* Pending orders badge */}
            {!newOrderAlert && pendingOrdersCount > 0 && <button onClick={() => onNavigate?.('pedidos')} className="flex items-center gap-1.5 px-2 lg:px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-xs font-bold text-blue-700 hover:bg-blue-100 transition-colors shadow-sm" data-id="element-81">
                <ShoppingCartIcon className="w-3.5 h-3.5" data-id="element-82" />
                <span className="hidden sm:inline" data-id="element-83">
                  {pendingOrdersCount} pendiente
                  {pendingOrdersCount > 1 ? 's' : ''}
                </span>
                <span className="sm:hidden" data-id="element-84">{pendingOrdersCount}</span>
              </button>}

            {/* Bell with notification dropdown */}
            <div className="relative" ref={notifRef} data-id="element-85">
              <button onClick={handleOpenNotif} className={`relative p-2 lg:p-2.5 rounded-xl transition-all duration-200 ${notifOpen ? 'text-[#2952C4] bg-blue-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} data-id="element-86">
                <BellIcon className="w-5 h-5" data-id="element-87" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 lg:top-1.5 lg:right-1.5 min-w-[16px] h-[16px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 ring-2 ring-white shadow-sm" data-id="element-88">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>}
              </button>

              {notifOpen && <div className="absolute right-0 top-full mt-3 w-80 bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-89">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50 backdrop-blur-sm" data-id="element-90">
                    <div className="flex items-center gap-2" data-id="element-91">
                      <BellIcon className="w-4 h-4 text-slate-600" data-id="element-92" />
                      <span className="text-sm font-bold text-slate-900" data-id="element-93">
                        Notificaciones
                      </span>
                      {unreadCount > 0 && <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full border border-rose-200" data-id="element-94">
                          {unreadCount} nuevas
                        </span>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-95">
                      {unreadCount > 0 && <button onClick={handleMarkAllRead} className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors hover:bg-blue-50" style={{
                    color: '#2952C4'
                  }} data-id="element-96">
                          <CheckCheckIcon className="w-3 h-3" data-id="element-97" />
                          Leídas
                        </button>}
                      <button onClick={handleCloseNotif} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-98">
                        <XIcon className="w-4 h-4" data-id="element-99" />
                      </button>
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-50 custom-scrollbar" data-id="element-100">
                    {visibleNotifs.length === 0 ? <div className="py-12 text-center" data-id="element-101">
                        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-102">
                          <BellIcon className="w-6 h-6 text-slate-300" data-id="element-103" />
                        </div>
                        <p className="text-sm font-medium text-slate-900" data-id="element-104">
                          Sin notificaciones
                        </p>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-105">
                          Estás al día con todo
                        </p>
                      </div> : visibleNotifs.slice(0, 15).map(notif => {
                  const cfg = notifTypeConfig[notif.type];
                  return <div key={notif.id} className={`flex items-start gap-3 px-4 py-3.5 transition-colors ${!notif.read ? 'bg-blue-50/40' : 'hover:bg-slate-50'}`} data-id="element-106">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm ${cfg.bg} ${cfg.color}`} data-id="element-107">
                              {cfg.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-108">
                              <div className="flex items-start justify-between gap-2" data-id="element-109">
                                <p className={`text-sm font-semibold ${!notif.read ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-110">
                                  {notif.title}
                                </p>
                                {!notif.read && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5 shadow-sm" style={{
                          backgroundColor: '#2952C4'
                        }} data-id="element-111" />}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate leading-relaxed" data-id="element-112">
                                {notif.message}
                              </p>
                              <p className="text-[10px] font-medium text-slate-400 mt-1.5" data-id="element-113">
                                {timeAgo(notif.timestamp)}
                              </p>
                            </div>
                          </div>;
                })}
                  </div>

                  <div className="border-t border-slate-100 p-2 bg-slate-50/50" data-id="element-114">
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setNotifOpen(false);
                }} className="w-full py-2 text-xs font-bold uppercase tracking-wide rounded-xl transition-colors hover:bg-white hover:shadow-sm text-slate-500 hover:text-[#2952C4]" data-id="element-115">
                      Gestionar preferencias
                    </button>
                  </div>
                </div>}
            </div>

            {/* User avatar dropdown */}
            <div className="relative" ref={dropdownRef} data-id="element-116">
              <button onClick={() => {
              setDropdownOpen(!dropdownOpen);
              setNotifOpen(false);
            }} className="flex items-center gap-3 pl-2 lg:pl-4 lg:ml-1 lg:border-l lg:border-slate-200 hover:opacity-80 transition-opacity group" data-id="element-117">
                <div className="text-right hidden lg:block" data-id="element-118">
                  <p className="text-sm font-bold text-slate-900 leading-tight" data-id="element-119">
                    {user?.nombre}
                  </p>
                  <p className="text-xs font-medium text-slate-500" data-id="element-120">
                    {user?.rolNombre}
                  </p>
                </div>
                <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center shadow-sm ring-2 ring-white overflow-hidden flex-shrink-0 group-hover:ring-blue-50 transition-all" style={{
                background: 'linear-gradient(135deg, #3B4BC8, #2D3A8C)'
              }} data-id="element-121">
                  {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-122" /> : <span className="text-xs lg:text-sm font-bold text-white" data-id="element-123">
                      {user?.nombre ? getInitials(user.nombre) : '??'}
                    </span>}
                </div>
                <ChevronDownIcon className={`hidden lg:block w-4 h-4 text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-[#2952C4]' : ''}`} data-id="element-124" />
              </button>

              {dropdownOpen && <div className="absolute right-0 top-full mt-3 w-60 bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-125">
                  <div className="px-5 py-4 bg-slate-50/50 border-b border-slate-100" data-id="element-126">
                    <div className="flex items-center gap-3" data-id="element-127">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm" style={{
                    background: 'linear-gradient(135deg, #3B4BC8, #2D3A8C)'
                  }} data-id="element-128">
                        {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-129" /> : <span className="text-sm font-bold text-white" data-id="element-130">
                            {user?.nombre ? getInitials(user.nombre) : '??'}
                          </span>}
                      </div>
                      <div className="min-w-0" data-id="element-131">
                        <p className="text-sm font-bold text-slate-900 truncate" data-id="element-132">
                          {user?.nombre}
                        </p>
                        <p className="text-xs font-medium text-slate-500" data-id="element-133">
                          {user?.rolNombre}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-1.5 space-y-0.5" data-id="element-134">
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 hover:text-[#2952C4] transition-colors" data-id="element-135">
                      <UserIcon className="w-4 h-4" data-id="element-136" />
                      Perfil
                    </button>
                    <button onClick={() => {
                  onNavigate?.('settings');
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 hover:text-[#2952C4] transition-colors" data-id="element-137">
                      <SettingsIcon className="w-4 h-4" data-id="element-138" />
                      Configuración
                    </button>
                  </div>
                  <div className="border-t border-slate-100 p-1.5" data-id="element-139">
                    <button onClick={() => {
                  logout();
                  setDropdownOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-rose-600 rounded-xl hover:bg-rose-50 transition-colors" data-id="element-140">
                      <LogOutIcon className="w-4 h-4" data-id="element-141" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
}